import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { TextButton } from '@xing-com/button';
import { UnauthorizedJobReason } from '@xing-com/crate-common-graphql-types';
import { Redirect, useParams } from '@xing-com/crate-core-router';
import {
  getXingJobsSlugPath,
  XING_JOBS_SEARCH_KI_PATH,
} from '@xing-com/crate-core-routes';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { ApplyButton } from '@xing-com/crate-jobs-components-apply-button';
import { SEARCH_STATE_PARAMS } from '@xing-com/crate-jobs-constants';
import { JobNotAvailable } from '@xing-com/crate-jobs-domain-job-details-components-error';
import { RegisterVisitDocument } from '@xing-com/crate-jobs-graphql';
import { getApplicationData } from '@xing-com/crate-jobs-helpers';
import { useJobTeaser } from '@xing-com/crate-jobs-hooks';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { JobTeaserCard } from '@xing-com/crate-jobs-xinglets';
import { HeaderMinimal } from '@xing-com/crate-layout-header-minimal';
import { TopBar } from '@xing-com/crate-layout-top-bar';
import { NoSSRLoggedInSuspense } from '@xing-com/crate-utils-no-ssr';
import { useHost } from '@xing-com/crate-xinglet';
import { GridContainer, GridItem } from '@xing-com/grid';
import { IconExternalLink } from '@xing-com/icons';
import { NotOnLarge } from '@xing-com/layout-primitives';
import { mediaWideNavless } from '@xing-com/layout-tokens';
import { spaceL, spaceXL, spaceXXL } from '@xing-com/tokens';
import { BodyCopy, Headline } from '@xing-com/typography';

import { ReducedJobDetailsLoading } from './reduced-job-details-loading';

const commonTrackingProps = {
  eventSchema: 'basic',
  page: 'jobs/detail_direct_apply',
} as const;

export const ReducedJobDetails: React.FC = () => (
  <NoSSRLoggedInSuspense fallback={<ReducedJobDetailsLoading />}>
    <ReducedJobDetailsInner />
  </NoSSRLoggedInSuspense>
);

export const ReducedJobDetailsInner: FC = () => {
  const host = useHost();
  const trackNwt = useJobsNwtTracking();
  const [registerVisitMutation] = useMutation(RegisterVisitDocument);

  const { isLoggedIn } = useLoginState();
  const { slug } = useParams<{ slug: string }>();
  const { job, jobId, error } = useJobTeaser(slug);

  useEffect(() => {
    const isValidJob =
      !!job?.globalId &&
      !!job?.id &&
      job?.application?.__typename === 'UrlApplication';

    if (isValidJob) {
      trackNwt({
        ...commonTrackingProps,
        event: 'viewed_screen',
        itemUrn: job?.globalId,
      });
      trackNwt({
        ...commonTrackingProps,
        event: 'job_detail_viewed',
        itemUrn: job?.globalId,
      });
      registerVisitMutation({
        variables: {
          jobId: job.id,
          originalUrl: location.href,
          referrer: document.referrer,
        },
      });
    }
  }, [
    job?.application?.__typename,
    job?.globalId,
    job?.id,
    registerVisitMutation,
    trackNwt,
  ]);

  if (!job) {
    if (error === UnauthorizedJobReason.Private && !isLoggedIn) {
      return host.redirectToLogin();
    } else {
      const geoFenced = error === UnauthorizedJobReason.GeoFenced;
      return <JobNotAvailable geoFenced={geoFenced} jobId={jobId} />;
    }
  }

  if (job.application?.__typename !== 'UrlApplication') {
    return <Redirect to={getXingJobsSlugPath({ slug: job.slug })} />;
  }

  const trackSearchClick = (): void => {
    trackNwt({
      ...commonTrackingProps,
      event: 'opened',
      element: 'job_search',
      itemUrn: job.globalId,
    });
  };

  const searchPath = `${XING_JOBS_SEARCH_KI_PATH}?${new URLSearchParams({
    [SEARCH_STATE_PARAMS.keywords]: job.title,
  }).toString()}`;

  return (
    <>
      <HeaderMinimal showOnSmallerScreens />
      <NotOnLarge>
        <TopBar type="logo" hideLoggedOut />
      </NotOnLarge>
      <Container variant="Narrow">
        <GridItem>
          <Headline size="xxlarge">
            <FormattedMessage id="JOBS_JOB_DETAILS_REDUCED_PAGE_TITLE" />
          </Headline>
          <BodyCopy>
            <FormattedMessage id="JOBS_JOB_DETAILS_REDUCED_PAGE_BODY" />
          </BodyCopy>
          <CardContainer>
            <JobTeaserCard
              job={job}
              url={job.url}
              matchingHighlights={job.matchingHighlights}
              aria-label={job.title}
              nwtTrackingOptionsBookmarkToggle={commonTrackingProps}
              hideApplyButton
            />
          </CardContainer>
          <ButtonContainer>
            <ApplyButton
              icon={IconExternalLink}
              application={{ ...getApplicationData(job), prioritized: true }} //We want to avoid opening the LO external apply flow
              trackingProps={commonTrackingProps}
            />
          </ButtonContainer>
          <ButtonContainer>
            <TextButton
              size="medium"
              to={searchPath}
              onClick={trackSearchClick}
            >
              <FormattedMessage id="JOBS_JOB_DETAILS_REDUCED_PAGE_SEARCH" />
            </TextButton>
          </ButtonContainer>
        </GridItem>
      </Container>
    </>
  );
};

const Container = styled(GridContainer)`
  margin: ${spaceXL} auto 0;
  padding: 0 ${spaceL};

  @media ${mediaWideNavless} {
    margin-top: ${spaceXXL};
  }
`;

const CardContainer = styled.div`
  margin-top: ${spaceXL};
  margin-bottom: ${spaceXL};

  @media ${mediaWideNavless} {
    margin-bottom: ${spaceXXL};
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin-bottom: ${spaceL};

  & button,
  & a {
    width: 100%;
  }
`;
